import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { EventService } from './event.service';

/**
 * The Twilio business number guests send their photos to. Kept here rather than
 * inline at each call site — event-detail used to be the only one, and a second
 * copy of a phone number is a silent way to end up with two of them.
 */
export const WHATSAPP_UPLOAD_NUMBER = '18885594643';

export interface WhatsappUploadLink {
  /** wa.me link with the event code already typed into the message box. */
  url: string;
  /** The bare code, for a host who wants to write it out themselves. */
  code: string;
  /** ISO timestamp the code stops working. Absent if the API didn't return one. */
  expiresAt?: string;
  /** Human-readable "valid until …", for showing next to the link — same string embedded in the WhatsApp message text, so host and guest see the same number. */
  validityLabel?: string;
}

/**
 * "Sep 18, 3:45 PM GMT+10" — used both in-app and inside the message text
 * itself, so whoever ends up with the link (host or the guest it's forwarded
 * to) sees the same deadline. The zone is spelled out because a guest may be
 * somewhere else. Same format the Lambdas use (formatValidUntil in
 * pwa-generate-whatsapp-token and pwa_create_event), which get this browser's
 * zone from setHostTimeZone in EventService.
 */
export function formatValidityLabel(expiresAt?: string): string | undefined {
  if (!expiresAt) return undefined;
  const date = new Date(expiresAt);
  if (isNaN(date.getTime())) return undefined;
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
}

/** Default validity for a multi-use invite link, if the caller doesn't pick one: 7 days. */
export const DEFAULT_INVITE_LINK_VALIDITY_MINUTES = 60 * 24 * 7;

/** A stored invite link is reused only while it has at least this long left, so a guest never gets one that is about to expire. */
const INVITE_LINK_MIN_REMAINING_MS = 24 * 60 * 60 * 1000;
const INVITE_LINK_STORAGE_PREFIX = 'wa_invite_link_';

/**
 * Builds the "send your photos on WhatsApp" link for an event.
 *
 * The code identifies the *event*; whoever sends the message is identified by
 * their own phone number (which is what the compliance notice on event-detail
 * tells them). That is what makes this shareable: the host hands the same link
 * to guests rather than using it only themselves.
 *
 * Minting needs the host's token, so this can only run on a host surface — the
 * guest landing pages are anonymous and could never fetch it. The link has to
 * travel out inside the invite.
 */
@Injectable({ providedIn: 'root' })
export class WhatsappUploadService {

  readonly enabled = environment.features.whatsappUpload;
  /** Separate switch for the multi-use invite link — can be toggled independently of the quick-upload button above. */
  readonly inviteLinkEnabled = environment.features.whatsappInviteLink;

  /** Requests still in flight, by event, so a prefetch and the invite screen share one token. */
  private inviteLinkRequests = new Map<string, Promise<WhatsappUploadLink | null>>();

  constructor(private eventService: EventService) { }

  /** The message the guest ends up sending. Same wording as event-detail's. */
  private composeMessage(code: string, eventName: string, validityLabel?: string): string {
    const validity = validityLabel ? ` (valid until ${validityLabel})` : '';
    return `Picaggo event code : ${code} for event: *${eventName}*. Send this message to get started${validity}`;
  }

  /**
   * Opens on the instruction, since the text lands pre-filled and guests
   * otherwise stare at it without pressing send. The bot finds the code
   * anywhere in the text; the "don't change" line stops guests editing it out.
   * No expiry line: the guest only needs it to work now, and the bot answers
   * if it doesn't.
   */
  private composeInviteMessage(code: string, eventName: string): string {
    return `👉 Tap SEND to join *${eventName}* and share your photos. Don't change this code: ${code}`;
  }

  /**
   * Resolves to null on any failure, including the feature being off. Callers
   * hide the row rather than showing a broken one — an invite that goes out
   * without the WhatsApp line is still a working invite.
   */
  async getUploadLink(eventId: any, eventName: string): Promise<WhatsappUploadLink | null> {
    if (!this.enabled || !eventId) return null;

    try {
      const res: any = await firstValueFrom(await this.eventService.generateWhatsappToken(eventId));
      if (!res) return null;

      const validityLabel = formatValidityLabel(res.expiresAt);

      // Prefer a finished link from the API — once it starts sending one, it's
      // a short redirect rather than the long wa.me?text=... this reconstructs.
      // Checked before `token` in case the API sends both during rollout.
      if (res.url) return { url: res.url, code: res.token || '', expiresAt: res.expiresAt, validityLabel };

      if (res.token) {
        const text = encodeURIComponent(this.composeMessage(res.token, eventName || 'our event', validityLabel));
        return { url: `https://wa.me/${WHATSAPP_UPLOAD_NUMBER}?text=${text}`, code: res.token, expiresAt: res.expiresAt, validityLabel };
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Multi-use "join the event + upload via WhatsApp" link — for sending out
   * as part of an invite, as opposed to getUploadLink's single-use,
   * host-only quick-upload code. Same guest-facing mechanics once redeemed
   * (session per phone number); the difference is entirely in how the
   * server-side token behaves (see generate-whatsapp-token: token_type
   * INVITE vs SINGLE_USE).
   *
   * The code is random and only works once the server has stored it, so it
   * can't be built here. Instead a minted link is kept per event and handed
   * back until it gets close to expiring, so a reopened invite screen has it
   * instantly rather than minting a fresh token on every open.
   */
  getInviteLink(
    eventId: any,
    eventName: string,
    validityMinutes: number = DEFAULT_INVITE_LINK_VALIDITY_MINUTES
  ): Promise<WhatsappUploadLink | null> {
    if (!this.inviteLinkEnabled || !eventId) return Promise.resolve(null);
    // Only the default validity is stored; a custom one always mints its own.
    const reusable = validityMinutes === DEFAULT_INVITE_LINK_VALIDITY_MINUTES;
    const key = String(eventId);

    if (reusable) {
      const stored = this.readStoredInviteLink(key);
      if (stored) return Promise.resolve(stored);
      const inFlight = this.inviteLinkRequests.get(key);
      if (inFlight) return inFlight;
    }

    const request = this.mintInviteLink(eventId, eventName, validityMinutes).then(link => {
      if (reusable && link) this.storeInviteLink(key, link);
      return link;
    });
    if (reusable) {
      this.inviteLinkRequests.set(key, request);
      request.finally(() => this.inviteLinkRequests.delete(key));
    }
    return request;
  }

  /**
   * Starts minting as soon as an event exists, so the link is usually back by
   * the time the invite screen asks for it. Fire and forget.
   */
  prefetchInviteLink(eventId: any, eventName: string): void {
    this.getInviteLink(eventId, eventName).catch(() => { /* the invite screen retries */ });
  }

  /**
   * Stores the link create-event minted alongside the event (`whatsapp_invite`
   * in its response), so the invite screen finds it with no request at all.
   * Returns false when there is nothing usable to store.
   */
  seedInviteLink(eventId: any, minted: { url?: string; token?: string; expiresAt?: string } | null | undefined): boolean {
    if (!this.inviteLinkEnabled || !eventId || !minted?.url || !minted.expiresAt) return false;
    this.storeInviteLink(String(eventId), {
      url: minted.url,
      code: minted.token || '',
      expiresAt: minted.expiresAt,
      validityLabel: formatValidityLabel(minted.expiresAt),
    });
    return true;
  }

  private readStoredInviteLink(key: string): WhatsappUploadLink | null {
    try {
      const link: WhatsappUploadLink | null = JSON.parse(localStorage.getItem(INVITE_LINK_STORAGE_PREFIX + key) || 'null');
      const expiresMs = link?.expiresAt ? new Date(link.expiresAt).getTime() : NaN;
      // The label is rebuilt rather than read back: links stored before it
      // carried a time zone would otherwise show the old, unlabelled time.
      if (link?.url && expiresMs - Date.now() > INVITE_LINK_MIN_REMAINING_MS) {
        return { ...link, validityLabel: formatValidityLabel(link.expiresAt) };
      }
    } catch { /* unreadable: mint a new one */ }
    return null;
  }

  private storeInviteLink(key: string, link: WhatsappUploadLink): void {
    // No expiry means no way to tell when it stops working, so don't keep it.
    if (!link.expiresAt) return;
    try {
      localStorage.setItem(INVITE_LINK_STORAGE_PREFIX + key, JSON.stringify(link));
    } catch { /* storage full or blocked: the next open just mints again */ }
  }

  private async mintInviteLink(eventId: any, eventName: string, validityMinutes: number): Promise<WhatsappUploadLink | null> {
    try {
      const res: any = await firstValueFrom(
        await this.eventService.generateWhatsappToken(eventId, 'INVITE', validityMinutes)
      );
      if (!res) return null;

      const validityLabel = formatValidityLabel(res.expiresAt);

      if (res.url) return { url: res.url, code: res.token || '', expiresAt: res.expiresAt, validityLabel };

      if (res.token) {
        const text = encodeURIComponent(this.composeInviteMessage(res.token, eventName || 'our event'));
        return { url: `https://wa.me/${WHATSAPP_UPLOAD_NUMBER}?text=${text}`, code: res.token, expiresAt: res.expiresAt, validityLabel };
      }

      return null;
    } catch {
      return null;
    }
  }
}
